import { useEffect, useState } from "react";
import { EditionListDto } from "../../../dtos/edition/EditionListDto";
import { createEditionPaymentAcc, createFreeSubscription, getAllEdition } from "../../../service/edition/EditionService";
import { useDispatch } from "react-redux";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { LoggedInUserData } from "../../../shared/functions/LoggedInUserData";
import { CreatePaymentDto } from "../../../dtos/payment/CreatePaymentDto";
import { loadStripe } from "@stripe/stripe-js";
import moment from "moment";
import { FEATURES_KEYS } from "../../../shared/constant/Features";
import { useNavigate } from "react-router-dom";
import { SelectButton } from "primereact/selectbutton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { useSubscriptionGuard } from "../../../utils/useSubscriptionGuard";
import { clearAuthStorage } from "../../../utils/storageUtils";
import { getTenantById } from "../../../service/tenant/TenantService";
import { setEdition } from "../../../store/slices/editionSlice";

export const SubscriptionPlan = () => {
  const [allSubscriptions, setAllSubscriptions] = useState<EditionListDto[]>([]);
  const dispatch = useDispatch();
  const [userDetails, setUserDetails] = useState<any>(null);
  const navigate = useNavigate();
  const { isExpired } = useSubscriptionGuard();

  const items = [
    { name: "Weekly", value: 1 },
    { name: "Monthly", value: 2 },
    { name: "Yearly", value: 3 },
  ];

  const [subscriptionType, setSubscriptionType] = useState(items[0]?.value);

  const getAllSubscription = async () => {
    getAllEdition(dispatch)
      .then((res) => {
        if (!res) return;
        setAllSubscriptions(res);
      })
      .catch((err) => {
        console.log(err);
      });
  };
  const handleSelect = async (editionId: number, amount: number) => {
    if (userDetails == null) return;

    const tenantId = localStorage.getItem("tenantId");


    if (tenantId == null) {
      throw new Error("Invalid tenant ID");
    }

    if (amount === 0) {
      try {
        await createFreeSubscription(dispatch, {
          tenantId: parseInt(tenantId),
          editionId: editionId,
          endDate: null
        });
        const tenant = await getTenantById(tenantId, dispatch);
        if (tenant?.editionId) {
          localStorage.setItem("editionId", tenant.editionId.toString());

          dispatch(
            setEdition({
              editionId: tenant.editionId,
            })
          );
        }
        // Redirect to dashboard or home page
        navigate("/dashboard");
      } catch (error) {
        console.error("Error subscribing to free plan:", error);
        // Show error message to user
      }
      return;
    }

    const stripe = await loadStripe(
      process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string
    );

    const props: CreatePaymentDto = {
      name: userDetails.name,
      surName: userDetails.surName,
      phoneNumber: userDetails.phoneNumber,
      email: userDetails.emailAddress,
      editionId: editionId,
      endDate: moment().add(1, "months").format("YYYY-MM-DD"),
      amount: amount,
    };

    const session = await createEditionPaymentAcc(dispatch, props);
    await stripe?.redirectToCheckout({
      sessionId: session.sessionId,
    });
  };

  const getUser = async () => {
    LoggedInUserData(dispatch)
      .then((user) => setUserDetails(user))
      .catch(() => setUserDetails(null));
  };

  const handleLogout = () => {
    navigate("/login");
    clearAuthStorage();
  };

  useEffect(() => {
    getAllSubscription();
    getUser();
  }, []);

  // Get the appropriate amount based on subscription type
  const getAmount = (subscription: EditionListDto) => {
    if (subscriptionType === 1) return subscription.weeklyAmount;
    if (subscriptionType === 2) return subscription.monthlyAmount;
    return subscription.yearlyAmount;
  };

  return (
    <div className="p-6 font-sans">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Choose Your Plan</h2>

        <div className="flex justify-center mb-8">
          <SelectButton
            value={subscriptionType}
            onChange={(e) => setSubscriptionType(e.value)}
            optionLabel="name"
            options={items}
            className="bg-white border border-gray-200 rounded-md shadow-sm"
          />
        </div>
        {isExpired && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded text-center">
            ⚠️ <strong>Your subscription plan has ended.</strong>
            Please renew to continue using the application.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allSubscriptions.map((subscription, index) => (
            <Card
              key={index}
              className={index === 1 ? "border-blue-500 border-2" : ""}
              title={subscription.displayName}
            >

              {/* FREE badge - centered vertically */}
              {subscription.yearlyAmount === 0 &&
                subscription.monthlyAmount === 0 &&
                subscription.weeklyAmount === 0 && (
                  <div className="flex justify-center items-center h-16 my-4">
                    <span className="inline-block bg-green-100 text-green-800 text-lg font-bold px-6 py-2 rounded-full">
                      FREE
                    </span>
                  </div>
                )}

              {/* Add description here */}
              {subscription.description && (
                <div className="mb-2">
                  <p className="text-gray-600 text-l leading-relaxed">
                    {subscription.description}
                  </p>
                </div>
              )}
              <div className="mb-6">
                {subscription.features?.length > 0 && (
                  <ul className="space-y-2">
                    {subscription.features.map((feature) => {
                      const featureDetails = FEATURES_KEYS[feature?.name];
                      return (
                        <li key={feature?.name} className="flex items-start">
                          <span className="text-emerald-500 mr-2 mt-0.5">
                            <FontAwesomeIcon icon={faCheck} />
                          </span>
                          <span className="text-gray-700">{featureDetails?.displayName}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="mt-auto">
                <div className="text-center mb-6">
                  <span className="text-3xl font-bold text-gray-800 font-mono">
                    ${getAmount(subscription)}
                  </span>
                  <span className="text-gray-500 ml-1">
                    /{subscriptionType === 1 ? 'week' : subscriptionType === 2 ? 'month' : 'year'}
                  </span>
                </div>

                <Button
                  // variant="primary" 
                  size="lg"
                  className="w-full"
                  onClick={() => handleSelect(subscription.id, getAmount(subscription))}
                >
                  {getAmount(subscription) === 0 ? 'Subscribe Free' : 'Get Started'}
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Button
            // variant="secondary" 
            size="md"
            onClick={handleLogout}
          >
            Go to Login
          </Button>
        </div>
      </div>
    </div>
  );
};